/** Counts media progress bounded by monotonic elapsed time, excluding seeks, pauses and stalls. */
export function createPlaybackTracker({ emit, now = () => performance.now() }) {
  let session, lastPosition = 0, lastWall = 0, running = false;
  const send = type => {
    if (!session || session.sent.has(type)) return;
    session.sent.add(type);
    emit(type, session, { listened_seconds: session.listened });
  };
  const tick = (position, duration, rate = 1) => {
    const wall = now();
    if (session && running) {
      const mediaDelta = position - lastPosition;
      const elapsed = Math.max(0, (wall - lastWall) / 1000) * Math.max(0, rate);
      // A discontinuity cannot manufacture listening even if a seek event is late.
      if (mediaDelta >= 0 && mediaDelta <= elapsed + .5) session.listened += Math.min(mediaDelta, elapsed);
      if (duration > 0 && session.listened >= Math.min(30, duration * .5)) send('listen_30s');
      if (duration > 0 && session.listened >= duration * .9) send('completion');
    }
    lastPosition = position; lastWall = wall;
  };
  return {
    select(next, userInitiated = true) {
      if (session?.started && userInitiated && now() - session.startedAt < 10000) send('skip');
      session = { ...next, listened: 0, started: false, startedAt: 0, sent: new Set() };
      running = false; lastPosition = 0; lastWall = now();
    },
    start(position) {
      if (!session) return;
      if (!session.started) { session.started = true; session.startedAt = now(); send('play_start'); }
      running = true; lastPosition = position; lastWall = now();
    },
    tick,
    suspend(position, duration, rate) { tick(position, duration, rate); running = false; },
    seek(position) { lastPosition = position; lastWall = now(); },
    reset() { session = undefined; running = false; },
  };
}

/** A retry keeps the complete original body (including event_id and attribution). */
export async function deliverGrowthEvent(body, post, wait = ms => new Promise(resolve => setTimeout(resolve, ms))) {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await post(body);
      if (response.code === 200) return response.data; // accepted=false is terminal, including duplicate_event_id.
      if (response.code < 500 && response.code !== 429) return;
    } catch (error) {
      const status = error?.response?.status;
      if (status && status < 500 && status !== 429) return;
    }
    if (attempt < 2) await wait(500 * (attempt + 1));
  }
}
